/**
 * Question positioning utilities for consistent placement across recording and playback
 */

export interface QuestionPositionParams {
  canvasWidth: number;
  canvasHeight: number;
  imgWidth: number;
  imgHeight: number;
  parentImageHeight: number;
  isOpticalMode: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isLandscape: boolean;
  savedMetadata?: any;
  parentImg?: any;
  toolbarHeight?: number;
  toolbarWidth?: number; // For landscape mode
  userMargins?: { // User-defined margins from device preferences
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
}

export interface QuestionPosition {
  scale: number;
  parentScale: number;
  left: number;
  top: number;
  questionTop: number;
  frameHeight: number;
}

/**
 * Calculate question position for playback - uses exact saved metadata
 */
export const calculateQuestionPositionForPlayback = (params: QuestionPositionParams): QuestionPosition => {
  const {
    imgWidth, imgHeight, parentImageHeight, savedMetadata, parentImg
  } = params;

  // For playback, use exact saved position
  if (savedMetadata && savedMetadata.scale && savedMetadata.left !== undefined && savedMetadata.top !== undefined) {
    
    let parentScale = 1;
    if (parentImg && parentImageHeight > 0) {
      parentScale = savedMetadata.scale * (imgWidth / (parentImg.width || 1));
    }
    
    const frameHeight = imgHeight * savedMetadata.scale + 
      (parentImageHeight > 0 ? parentImageHeight * parentScale + 20 : 0);
    
    return {
      scale: savedMetadata.scale,
      parentScale,
      left: savedMetadata.left,
      top: savedMetadata.top,
      questionTop: savedMetadata.top,
      frameHeight
    };
  }
  
  // Fallback - just use default position
  
  return {
    scale: 0.5,
    parentScale: 0.5,
    left: 40,
    top: 40,
    questionTop: 40,
    frameHeight: imgHeight * 0.5
  };
};

export const calculateQuestionPosition = (params: QuestionPositionParams): QuestionPosition => {
  const {
    canvasWidth, canvasHeight, imgWidth, imgHeight, parentImageHeight,
    isOpticalMode, isMobile, isTablet, isLandscape, savedMetadata, parentImg,
    toolbarHeight = 0, toolbarWidth = 0, userMargins
  } = params;

  // Detect device type
  const isTabletDevice = window.innerWidth >= 768;
  const isLandscapeMode = window.innerWidth > window.innerHeight;
  
  // Calculate available space (accounting for UI elements)
  // Use actual toolbar width if provided, otherwise use default
  const opticalFormWidth = isOpticalMode && isLandscapeMode ? (toolbarWidth || 250) : 0;
  
  // Use the actual toolbar height passed from the component
  const actualToolbarHeight = toolbarHeight || 0;
  
  // Simple margins for consistent positioning
  // Default marginTop accounts for exit button (25px) + 25px spacing
  let marginTop = 50;
  let marginBottom = 80;
  let marginLeft = 40;
  let marginRight = 40;
  
  // Simple positioning: leave space for drawing
  if (isLandscapeMode) {
    // Landscape: question on left side, leave right side for drawing
    marginRight = canvasWidth * 0.5; // Keep original for drawing space
    marginBottom = canvasHeight * 0.3;
  } else {
    // Portrait: ensure minimum 250px bottom space
    marginBottom = Math.max(250, canvasHeight * 0.35);
  }
  
  // Mobile-specific adjustments
  if (isMobile) {
    if (!isLandscapeMode) {
      // Portrait mobile - ensure enough bottom space
      const minBottomSpace = 250; // Minimum 250px from bottom
      marginBottom = Math.max(minBottomSpace, 20 + actualToolbarHeight);
      marginTop = 50; // Increased for camera/notch clearance
      
      // Standard side margins for portrait mode
      marginLeft = 30; // Consistent left margin
      marginRight = 30; // Consistent right margin
    } else {
      // Landscape mobile - adjust margins for better space usage
      marginTop = 50; // Increased for camera in landscape too     
      marginBottom = 20 + actualToolbarHeight; // Account for actual toolbar height
      
      // Use consistent margins for both optical and normal modes
      marginLeft = 40; // Consistent left margin
      
      if (isOpticalMode) {
        // In landscape optical mode, account for side panel if needed
        marginRight = Math.max(marginRight, opticalFormWidth + 20);
      }
      // else keep the marginRight from above calculation
    }
  } else if (isTablet) {
    // Tablet-specific margins
    if (isLandscapeMode) {
      // Landscape tablet - margins already set above (25% bottom, 50% right)
      marginTop = 25;
      marginLeft = 25;
      // marginRight and marginBottom already set above
      
      if (isOpticalMode) {
        // Account for optical form on the right
        marginRight = Math.max(marginRight, opticalFormWidth + 30);
      }
    } else {
      // Portrait tablet - standard side margins
      marginLeft = 40;
      marginRight = 40;
      marginTop = 40; // More space on tablets
      // Bottom margin already set above
      if (isOpticalMode) {
        // Maintain at least 50% bottom margin for optical mode
        marginBottom = Math.max(marginBottom, canvasHeight * 0.50);
      }
    }
  } else {
    // Desktop
    // Account for exit button height (25px) + 25px spacing
    marginTop = 50;
    
    if (isLandscapeMode) {
      // Landscape desktop - margins already set above (25% bottom, 50% right)
      // No additional changes needed
    } else {
      // Portrait desktop (rare case) - apply 50% rule for consistency
      marginBottom = Math.max(canvasHeight * 0.50, 100);
      marginLeft = 40;
      marginRight = 40;
    }
  }
  
  // Apply user-defined margins if available
  if (userMargins) {
    // Apply user margins with safety limits
    if (userMargins.top !== undefined) {
      marginTop = Math.max(25, Math.min(100, userMargins.top)); // Min 25 for exit button, max 100
    }
    if (userMargins.bottom !== undefined) {
      marginBottom = Math.max(50, Math.min(500, userMargins.bottom)); // Min 50, max 500
    }
    if (userMargins.left !== undefined) {
      marginLeft = Math.max(10, Math.min(100, userMargins.left)); // Min 10, max 100
    }
    if (userMargins.right !== undefined) {
      marginRight = Math.max(10, Math.min(100, userMargins.right)); // Min 10, max 100
    }
  }
  
  // Calculate maximum available space
  const maxAvailableWidth = canvasWidth - marginLeft - marginRight;
  const maxAvailableHeight = canvasHeight - marginTop - marginBottom;
  
  // // Log available space calculation for debugging
  // if (isMobile || isTablet) {
  //   console.log('Canvas space calculation:', {
  //     canvasWidth,
  //     canvasHeight,
  //     marginTop,
  //     marginBottom,
  //     marginLeft,
  //     marginRight,
  //     actualToolbarHeight,
  //     opticalFormWidth,
  //     maxAvailableWidth,
  //     maxAvailableHeight,
  //     widthPercentageUsed: (maxAvailableWidth / canvasWidth * 100).toFixed(1) + '%',
  //     heightPercentageUsed: (maxAvailableHeight / canvasHeight * 100).toFixed(1) + '%'
  //   });
  // }
  
  

  // Calculate scale to fit
  let effectiveImgHeight = imgHeight;
  let parentScale = 1;
  
  if (parentImg && parentImageHeight > 0) {
    // Calculate parent image scale to match question width
    parentScale = imgWidth > 0 ? (imgWidth / (parentImg.width || 1)) : 1;
    const scaledParentHeight = parentImageHeight * parentScale;
    
    // Add spacing between parent and question image (mobile-aware)
    let spacingBetweenImages = 20;
    if (isMobile) {
      spacingBetweenImages = isLandscapeMode ? 10 : 15;
    }
    effectiveImgHeight = imgHeight + scaledParentHeight + spacingBetweenImages;
  }
  
  // For mobile, add extra space to prevent cutoff and improve fit
  if (isMobile) {
    // Add 20% extra space for better fit on all mobile orientations
    effectiveImgHeight = effectiveImgHeight * 1.2;
  }
  
  const scaleX = maxAvailableWidth / imgWidth;
  const scaleY = maxAvailableHeight / effectiveImgHeight;
  let initialScale = Math.min(scaleX, scaleY);
  
  // Extra safety check - ensure the scaled image fits within canvas
  const projectedHeight = effectiveImgHeight * initialScale;
  if (projectedHeight > maxAvailableHeight) {
    console.warn('Image would overflow canvas, adjusting scale:', {
      projectedHeight,
      maxAvailableHeight,
      initialScale,
      adjustedScale: maxAvailableHeight / effectiveImgHeight
    });
    initialScale = (maxAvailableHeight / effectiveImgHeight) * 0.95; // 5% safety margin
  }
  
  // Smart scaling based on device
  if (isTablet) {
    // Tablet-specific scaling
    if (isLandscapeMode) {
      // Horizontal tablet - ensure 25% right margin and 10% bottom margin
      // Scale is already calculated based on available space which includes margins
      // Don't reduce further with safety factor for tablets
      initialScale = Math.min(scaleX, scaleY);
    } else {
      // Portrait tablet
      initialScale = Math.min(scaleX, scaleY);
    }
  } else if (isMobile) {
    // Mobile scaling logic - scale already considers margins via maxAvailable calculations
    // Don't further reduce scale excessively
    if (isTabletDevice) {
      // iPad/Tablet: use calculated scale without further reduction
      // Scale is already properly constrained by available space
      initialScale = initialScale;
    } else {
      // Phone - don't reduce scale further, it's already calculated to fit
      // The scale calculation already accounts for margins
      // No additional reduction needed
    }
    
    // For mobile, we need to ensure image fits within bounds
    // More flexible scale limits for very large questions
    const minScale = isTabletDevice ? 0.2 : 0.15;
    const maxScale = isTabletDevice ? 0.7 : 0.5;
    
    // Apply smaller safety factor
    initialScale = initialScale * 0.95;
    
    // Constrain scale between min and max
    initialScale = Math.max(minScale, Math.min(maxScale, initialScale));
    
    // Final check - ensure it fits with proper bottom margin
    const frameHeightEstimate = effectiveImgHeight * initialScale + 40; // Include frame padding
    const bottomPosition = marginTop + frameHeightEstimate;
    const remainingSpace = canvasHeight - bottomPosition;
    
    // For very large questions, be more aggressive
    const targetBottomSpace = 250;
    const maxAllowedHeight = canvasHeight - marginTop - targetBottomSpace;
    
    if (frameHeightEstimate > maxAllowedHeight) {
      // Need to reduce scale to fit
      const requiredScale = (maxAllowedHeight / (effectiveImgHeight + 40)) * 0.9; // 10% safety margin
      
      console.log('Mobile scale adjustment for large question:', {
        questionHeight: effectiveImgHeight,
        scaledHeight: frameHeightEstimate,
        maxAllowedHeight,
        canvasHeight,
        currentScale: initialScale,
        requiredScale,
        willFit: requiredScale > 0.1
      });
      
      // Allow very small scales for huge questions
      initialScale = Math.max(0.1, requiredScale);
    }
  } else {
    // Desktop
    initialScale = Math.min(initialScale, 1.2);
  }

  let scale = initialScale;
  let left = marginLeft;
  let top = marginTop;
  
  console.log('Question position calculation:', {
    imgWidth,
    imgHeight,
    effectiveImgHeight,
    canvasWidth,
    canvasHeight,
    marginTop,
    marginBottom,
    maxAvailableWidth,
    maxAvailableHeight,
    scaleX,
    scaleY,
    initialScale,
    finalScale: scale,
    isMobile,
    isTablet,
    estimatedBottom: marginTop + (effectiveImgHeight * scale) + 40
  });
  
  // Use saved metadata if available
  if (savedMetadata) {
    scale = savedMetadata.scale;
    left = savedMetadata.left;
    top = savedMetadata.top;
    
    // But still check mobile bottom constraint
    if (isMobile && !isLandscapeMode) {
      const frameHeightEstimate = imgHeight * scale + 
        (parentImageHeight > 0 ? parentImageHeight * scale + 20 : 0);
      const bottomPosition = top + frameHeightEstimate;
      const maxAllowedBottom = canvasHeight - 250;
      
      if (bottomPosition > maxAllowedBottom) {
        top = maxAllowedBottom - frameHeightEstimate;
        console.log('Adjusted saved position for mobile bottom constraint:', {
          originalTop: savedMetadata.top,
          adjustedTop: top,
          bottomPosition,
          maxAllowedBottom
        });
      }
    }
  }

  // Don't pre-calculate questionTop - let it be calculated during image positioning
  const questionTop = top;  // Will be adjusted later if parent exists

  const frameHeight = imgHeight * scale + 
    (parentImageHeight > 0 ? parentImageHeight * parentScale + 20 : 0);

  // Final boundary checks to ensure image stays completely within canvas
  const scaledWidth = imgWidth * scale;
  const scaledHeight = frameHeight; // Use full frame height for boundary check
  
  // Check right boundary
  if (left + scaledWidth > canvasWidth) {
    left = canvasWidth - scaledWidth;
  }
  
  // Check bottom boundary - ensure minimum space from bottom
  const minBottomSpace = isMobile && !isLandscapeMode ? 250 : marginBottom;
  const maxAllowedBottom = canvasHeight - minBottomSpace;
  
  if (top + scaledHeight > maxAllowedBottom) {
    top = maxAllowedBottom - scaledHeight;
    console.log('Bottom boundary adjustment:', {
      originalTop: top + scaledHeight - maxAllowedBottom,
      adjustedTop: top,
      scaledHeight,
      maxAllowedBottom,
      minBottomSpace
    });
  }
  
  // Ensure left and top are not negative
  left = Math.max(0, left);
  top = Math.max(0, top);

  return {
    scale,
    parentScale,
    left,
    top,
    questionTop,
    frameHeight
  };
};